/**
 * @fileoverview Date picker component for selecting dates
 * @module ctl-date-picker
 * @version 1.0.0
 * 
 * @description
 * A date picker component that provides an accessible and user-friendly
 * interface for selecting dates. Uses native date input with enhanced styling.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-date-picker label="Select Date"></ctl-date-picker>
 * 
 * @example
 * <!-- With min/max dates -->
 * <ctl-date-picker 
 *   label="Appointment Date" 
 *   min="2024-01-01" 
 *   max="2024-12-31"
 *   value="2024-06-15"
 * ></ctl-date-picker>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    --ctl-radius: var(--ctl-radius-lg);

    --ctl-color-focus: var(--ctl-color-brand);

    --ctl-field-height: var(--ctl-space-10);
    --ctl-pad-x: var(--ctl-space-3);
    --ctl-gap: var(--ctl-space-2);

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-1);
  }

  .label {
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-normal);
    color: var(--ctl-color-text);
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: var(--ctl-gap);
    height: var(--ctl-field-height);
    padding: 0 var(--ctl-pad-x);
    border-radius: var(--ctl-radius);
    border: var(--ctl-space-0-5) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    box-sizing: border-box;
    transition: border-color var(--ctl-transition-fast) ease, box-shadow var(--ctl-transition-fast) ease, background var(--ctl-transition-fast) ease, opacity var(--ctl-transition-fast) ease;
    position: relative;
  }

  :host([invalid]) .input-wrapper {
    border-color: var(--ctl-color-danger);
  }

  .input-wrapper:focus-within {
    border-color: var(--ctl-color-focus);
    box-shadow: var(--ctl-shadow-sm);
  }

  :host([disabled]) .input-wrapper {
    opacity: var(--opacity-60, 0.6);
    background: color-mix(in srgb, var(--color-black) 2%, transparent);
    cursor: not-allowed;
  }

  input[type="date"] {
    flex: 1;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: var(--font-size-sm, 0.875rem);
    color: var(--ctl-color-text);
    outline: none;
    padding: 0;
    min-width: 0;
  }

  input[type="date"]:disabled {
    cursor: not-allowed;
  }

  input[type="date"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: var(--opacity-60, 0.6);
    transition: opacity var(--transition-fast, 150ms ease-in-out);
  }

  input[type="date"]::-webkit-calendar-picker-indicator:hover {
    opacity: var(--ctl-opacity-default);
  }

  :host([disabled]) input[type="date"]::-webkit-calendar-picker-indicator {
    cursor: not-allowed;
    opacity: var(--ctl-opacity-disabled);
  }

  .prefix, .suffix {
    display: inline-flex;
    align-items: center;
    color: var(--ctl-color-muted);
    font-size: var(--font-size-sm, 0.875rem);
  }

  .prefix:empty, .suffix:empty {
    display: none;
  }

  .help {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
  }

  .error {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-danger);
  }

  .error:empty {
    display: none;
  }
</style>

<div class="container">
  <label class="label" part="label"></label>
  <div class="input-wrapper" part="input-wrapper">
    <span class="prefix" part="prefix">
      <slot name="prefix"></slot>
    </span>
    <input type="date" part="input" />
    <span class="suffix" part="suffix">
      <slot name="suffix"></slot>
    </span>
  </div>
  <div class="help" part="help"></div>
  <div class="error" part="error"></div>
</div>
`;

/**
 * Date Picker Web Component
 * 
 * @class CtlDatePicker
 * @extends HTMLElement
 * 
 * @attr {string} label - Label text for the date picker
 * @attr {string} value - Selected date value in YYYY-MM-DD format
 * @attr {string} min - Minimum selectable date in YYYY-MM-DD format
 * @attr {string} max - Maximum selectable date in YYYY-MM-DD format
 * @attr {string} placeholder - Placeholder text for the input
 * @attr {string} help - Help text displayed below the input
 * @attr {string} error - Error message displayed below the input
 * @attr {boolean} required - Whether the date picker is required
 * @attr {boolean} disabled - Whether the date picker is disabled
 * @attr {boolean} invalid - Whether the date picker has validation errors
 * @attr {string} name - Name attribute for form submission
 * 
 * @slot prefix - Content to display before the input
 * @slot suffix - Content to display after the input
 * 
 * @fires ctl-change - Dispatched when the date value changes
 * @fires ctl-change.detail.value - The new date value
 * @fires ctl-change.detail.previousValue - The previous date value
 * 
 * @csspart label - The label element
 * @csspart input-wrapper - The input wrapper container
 * @csspart input - The date input element
 * @csspart prefix - The prefix slot container
 * @csspart suffix - The suffix slot container
 * @csspart help - The help text element
 * @csspart error - The error message element
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color for labels and icons
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-focus - Focus state color
 * @cssprop --ctl-color-danger - Error state color
 * @cssprop --ctl-field-height - Input field height
 * @cssprop --ctl-radius - Border radius
 */
export class CtlDatePicker extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._input = this.shadowRoot.querySelector('input');
    this._label = this.shadowRoot.querySelector('.label');
    this._help = this.shadowRoot.querySelector('.help');
    this._error = this.shadowRoot.querySelector('.error');
    this._previousValue = '';
  }

  static get observedAttributes() {
    return ['label', 'value', 'min', 'max', 'placeholder', 'help', 'error', 
            'required', 'disabled', 'invalid', 'name'];
  }

  connectedCallback() {
    this._input.addEventListener('change', this._handleChange.bind(this));
    this._input.addEventListener('input', this._handleInput.bind(this));
    this._render();
  }

  disconnectedCallback() {
    this._input.removeEventListener('change', this._handleChange.bind(this));
    this._input.removeEventListener('input', this._handleInput.bind(this));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this._render();
    }
  }

  _handleChange(e) {
    const newValue = e.target.value;
    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: {
        value: newValue,
        previousValue: this._previousValue
      }
    }));
    this._previousValue = newValue;
  }

  _handleInput(e) {
    // Update the value attribute to keep in sync
    this.setAttribute('value', e.target.value);
  }

  _render() {
    // Label
    const label = this.getAttribute('label');
    if (label) {
      this._label.textContent = label;
      this._label.style.display = 'block';
    } else {
      this._label.style.display = 'none';
    }

    // Value
    const value = this.getAttribute('value');
    if (value !== null && this._input.value !== value) {
      this._input.value = value;
      this._previousValue = value;
    }

    // Min/Max
    const min = this.getAttribute('min');
    const max = this.getAttribute('max');
    if (min) this._input.min = min;
    if (max) this._input.max = max;

    // Placeholder
    const placeholder = this.getAttribute('placeholder');
    if (placeholder) {
      this._input.placeholder = placeholder;
    }

    // Name
    const name = this.getAttribute('name');
    if (name) {
      this._input.name = name;
    }

    // Required
    this._input.required = this.hasAttribute('required');

    // Disabled
    this._input.disabled = this.hasAttribute('disabled');

    // Help text
    const help = this.getAttribute('help');
    if (help) {
      this._help.textContent = help;
      this._help.style.display = 'block';
    } else {
      this._help.style.display = 'none';
    }

    // Error message
    const error = this.getAttribute('error');
    if (error) {
      this._error.textContent = error;
      this._error.style.display = 'block';
    } else {
      this._error.style.display = 'none';
    }
  }

  /**
   * Get the current date value
   * @returns {string} The current date in YYYY-MM-DD format
   */
  get value() {
    return this._input.value;
  }

  /**
   * Set the date value
   * @param {string} val - Date in YYYY-MM-DD format
   */
  set value(val) {
    this.setAttribute('value', val);
  }

  /**
   * Check if the date picker is valid
   * @returns {boolean} True if valid, false otherwise
   */
  checkValidity() {
    return this._input.checkValidity();
  }

  /**
   * Reset the date picker to empty
   */
  reset() {
    this._input.value = '';
    this.removeAttribute('value');
    this.removeAttribute('invalid');
  }
}

if (!customElements.get("ctl-date-picker")) {
  customElements.define("ctl-date-picker", CtlDatePicker);
}
